'use client';

import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
} from 'react';
import {
  useRouter,
  usePathname,
} from 'next/navigation';
import {
  useConfirmDialog,
} from './ConfirmDialogContext';

type NavigationConfirmationContextType = {
  registerDirtyState: (isDirty: boolean, options?: {
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
  }) => void;
  unregisterDirtyState: () => void;
};

const NavigationConfirmationContext = createContext<NavigationConfirmationContextType | undefined>(undefined);

export function NavigationConfirmationProvider({ children }: { children: React.ReactNode }) {
  const { confirm } = useConfirmDialog();
  const router = useRouter();
  const pathname = usePathname();

  const dirtyStateRef = useRef({
    isDirty: false,
    options: {
      title: 'Unsaved Changes',
      message: 'You have unsaved changes. Do you want to leave without saving?',
      confirmText: 'Stay',
      cancelText: 'Leave',
    },
  });

  const isConfirmingRef = useRef(false);
  const originalPushRef = useRef<typeof router.push | null>(null);
  const originalReplaceRef = useRef<typeof router.replace | null>(null);
  const originalBackRef = useRef<typeof router.back | null>(null);

  // Intercept router methods
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Store original methods
    originalPushRef.current = router.push;
    originalReplaceRef.current = router.replace;
    originalBackRef.current = router.back;

    // Override router.push
    router.push = async (href: string, options?: any) => {
      if (dirtyStateRef.current.isDirty && !isConfirmingRef.current) {
        isConfirmingRef.current = true;
        try {
          const confirmed = await confirm(dirtyStateRef.current.options);
          // confirmed = true means "Stay" was clicked, so DON'T navigate
          // confirmed = false means "Leave" was clicked, so DO navigate
          if (!confirmed && originalPushRef.current) {
            originalPushRef.current(href, options);
          }
        } finally {
          isConfirmingRef.current = false;
        }
      } else if (originalPushRef.current) {
        originalPushRef.current(href, options);
      }
    };

    // Override router.replace
    router.replace = async (href: string, options?: any) => {
      if (dirtyStateRef.current.isDirty && !isConfirmingRef.current) {
        isConfirmingRef.current = true;
        try {
          const confirmed = await confirm(dirtyStateRef.current.options);
          // confirmed = true means "Stay" was clicked, so DON'T navigate
          // confirmed = false means "Leave" was clicked, so DO navigate
          if (!confirmed && originalReplaceRef.current) {
            originalReplaceRef.current(href, options);
          }
        } finally {
          isConfirmingRef.current = false;
        }
      } else if (originalReplaceRef.current) {
        originalReplaceRef.current(href, options);
      }
    };

    // Override router.back
    router.back = async () => {
      if (dirtyStateRef.current.isDirty && !isConfirmingRef.current) {
        isConfirmingRef.current = true;
        try {
          const confirmed = await confirm(dirtyStateRef.current.options);
          // confirmed = true means "Stay" was clicked, so DON'T navigate
          // confirmed = false means "Leave" was clicked, so DO navigate
          if (!confirmed && originalBackRef.current) {
            originalBackRef.current();
          }
        } finally {
          isConfirmingRef.current = false;
        }
      } else if (originalBackRef.current) {
        originalBackRef.current();
      }
    };

    // Cleanup - restore original methods
    return () => {
      if (originalPushRef.current) router.push = originalPushRef.current;
      if (originalReplaceRef.current) router.replace = originalReplaceRef.current;
      if (originalBackRef.current) router.back = originalBackRef.current;
    };
  }, [router, confirm]);

  // Handle browser navigation (back/forward/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirtyStateRef.current.isDirty && !isConfirmingRef.current) {
        e.preventDefault();
        e.returnValue = dirtyStateRef.current.options.message || 'You have unsaved changes.';
        return dirtyStateRef.current.options.message || 'You have unsaved changes.';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const registerDirtyState = (isDirty: boolean, options = {}) => {
    dirtyStateRef.current = {
      isDirty,
      options: {
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Do you want to leave without saving?',
        confirmText: 'Stay',
        cancelText: 'Leave',
        ...options,
      },
    };
  };

  const unregisterDirtyState = () => {
    dirtyStateRef.current.isDirty = false;
  };

  return (
    <NavigationConfirmationContext.Provider value={{ registerDirtyState, unregisterDirtyState }}>
      {children}
    </NavigationConfirmationContext.Provider>
  );
}

export function useNavigationConfirmation() {
  const context = useContext(NavigationConfirmationContext);
  if (context === undefined) {
    throw new Error('useNavigationConfirmation must be used within a NavigationConfirmationProvider');
  }
  return context;
}